export const OPERATOR_DOCUMENT_ENTITY_CLASSES = [
  "sop",
  "runbook",
  "help-guide",
] as const;

export type OperatorDocumentEntityClass =
  (typeof OPERATOR_DOCUMENT_ENTITY_CLASSES)[number];

export const OPERATOR_DOCUMENT_CLASS_LABELS: Record<
  OperatorDocumentEntityClass,
  string
> = {
  sop: "SOP",
  runbook: "Runbook",
  "help-guide": "Help guide",
};

export const OPERATOR_DOCUMENT_ACCEPT =
  ".txt,.md,.log,.json,.csv,.yaml,.yml,.pdf,.doc,.docx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.webp,.tif,.tiff,text/plain,text/markdown,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,image/png,image/jpeg,image/gif,image/webp,image/tiff";

export function isOperatorDocumentEntityClass(
  value: string | null | undefined,
): value is OperatorDocumentEntityClass {
  return OPERATOR_DOCUMENT_ENTITY_CLASSES.includes(
    value as OperatorDocumentEntityClass,
  );
}

export function isOperatorCatalogDocument(doc: {
  entityClass?: string | null;
  tags?: string[] | null;
}): boolean {
  if (isOperatorDocumentEntityClass(doc.entityClass)) return true;
  return Boolean(
    doc.tags?.includes("sop") ||
      doc.tags?.includes("error-guide") ||
      doc.tags?.includes("runbook") ||
      doc.tags?.includes("help-guide"),
  );
}

export function operatorDocumentClassLabel(
  entityClass: string | null | undefined,
): string {
  if (isOperatorDocumentEntityClass(entityClass)) {
    return OPERATOR_DOCUMENT_CLASS_LABELS[entityClass];
  }
  if (entityClass === "mixed") return "Mixed";
  if (entityClass === "other") return "Other";
  return entityClass?.trim() || "Document";
}

export function containedTypesFromClassifications(
  classifications: string | null | undefined,
): string[] {
  if (!classifications?.trim()) return [];
  try {
    const parsed = JSON.parse(classifications) as { containedTypes?: unknown };
    return Array.isArray(parsed.containedTypes)
      ? parsed.containedTypes.filter((value): value is string => typeof value === "string")
      : [];
  } catch {
    return [];
  }
}

export function documentMatchesTypeFilter(
  doc: {
    entityClass?: string | null;
    classifications?: string | null;
    tags?: string[] | null;
  },
  filter: string,
): boolean {
  if (filter === "all") return true;
  if (filter === "operator") return isOperatorCatalogDocument(doc) || doc.entityClass === "mixed";
  if (doc.entityClass === filter) return true;
  return containedTypesFromClassifications(doc.classifications).includes(filter);
}

export function ragIndexStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case "PENDING":
      return "Index: pending";
    case "INDEXING":
      return "Index: indexing";
    case "READY":
      return "Index: ready";
    case "FAILED":
      return "Index: failed";
    case "EXCLUDED":
      return "Index: excluded";
    case "EXPIRED":
      return "Index: expired";
    default:
      return "Index: not queued";
  }
}
